import { useCallback, useEffect, useState } from "react";
import api from "../../api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field } from "../FormField";
import { eur } from "../../helpers";
import { Plus, Trash2, Pencil, Package } from "lucide-react";

const CATS = ["Elettrodomestici", "Arredo", "Cucina", "Biancheria", "Bagno", "Elettronica", "Sicurezza", "Esterno", "Consumabili", "Altro"];
const CONDS = ["nuovo", "buono", "usurato", "da_sostituire"];
const empty = { name: "", category: "Elettrodomestici", qty: 1, condition: "buono", location: "", value: "", notes: "" };

export default function InventoryTab({ apartmentId }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = useCallback(() => {
    api.get(`/apartments/${apartmentId}/detail`).then((r) => setItems(r.data.apartment?.inventory || [])).catch(() => {});
  }, [apartmentId]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.name) return toast.error("Nome obbligatorio");
    try {
      if (editId) await api.put(`/apartments/${apartmentId}/inventory/${editId}`, { ...form, qty: +form.qty, value: +(form.value || 0) });
      else await api.post(`/apartments/${apartmentId}/inventory`, { ...form, qty: +form.qty, value: +(form.value || 0) });
      toast.success("Salvato"); setForm(empty); setEditId(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const edit = (it) => { setEditId(it.id); setForm({ ...it, value: it.value || "" }); };
  const del = async (iid) => { await api.delete(`/apartments/${apartmentId}/inventory/${iid}`); load(); };

  const total = items.reduce((s, i) => s + (i.value || 0) * (i.qty || 1), 0);

  return (
    <Card className="border-slate-200 p-5" data-testid="detail-inventory">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="flex items-center gap-2 font-head font-semibold text-slate-900"><Package size={18} /> Inventario <span className="text-sm font-normal text-slate-400">({items.length} · valore {eur(total)})</span></h3>
      </div>

      <div className="mb-4 grid grid-cols-2 gap-2 rounded-lg bg-secondary/40 p-3 sm:grid-cols-4" data-testid="inventory-form">
        <Field label="Articolo"><Input data-testid="inv-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} /></Field>
        <Field label="Categoria">
          <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
            <SelectTrigger data-testid="inv-category"><SelectValue /></SelectTrigger>
            <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Q.tà"><Input type="number" data-testid="inv-qty" value={form.qty} onChange={(e) => setForm((f) => ({ ...f, qty: e.target.value }))} /></Field>
        <Field label="Condizione">
          <Select value={form.condition} onValueChange={(v) => setForm((f) => ({ ...f, condition: v }))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{CONDS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Ubicazione"><Input value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} /></Field>
        <Field label="Valore €"><Input type="number" value={form.value} onChange={(e) => setForm((f) => ({ ...f, value: e.target.value }))} /></Field>
        <Field label="Note" className="sm:col-span-2"><Input value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} /></Field>
        <div className="flex items-end gap-2 sm:col-span-4">
          <Button size="sm" className="bg-primary hover:bg-primary/90" data-testid="inv-save" onClick={save}><Plus size={14} className="mr-1" />{editId ? "Aggiorna" : "Aggiungi"}</Button>
          {editId && <Button size="sm" variant="outline" onClick={() => { setForm(empty); setEditId(null); }}>Annulla</Button>}
        </div>
      </div>

      <div className="space-y-1.5">
        {items.length === 0 && <p className="text-sm text-slate-400">Nessun articolo in inventario.</p>}
        {items.map((it) => (
          <div key={it.id} data-testid={`inv-${it.id}`} className="flex items-center justify-between rounded-lg border border-slate-200 p-2.5 text-sm">
            <div><span className="font-medium text-slate-800">{it.name}</span> <span className="text-slate-400">×{it.qty}</span>
              <div className="text-xs text-slate-400">{it.category} · {it.condition}{it.location ? ` · ${it.location}` : ""}{it.value ? ` · ${eur(it.value)}` : ""}</div></div>
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" data-testid={`inv-edit-${it.id}`} onClick={() => edit(it)}><Pencil size={13} /></Button>
              <Button size="sm" variant="ghost" className="text-rose-600" data-testid={`inv-del-${it.id}`} onClick={() => del(it.id)}><Trash2 size={13} /></Button>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
